/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react/display-name */
import React, {
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  Button,
  Col,
  Form,
  Input,
  PageHeader,
  Row,
  Select,
  Skeleton,
  Table,
} from 'antd'
import { SelectValue } from 'antd/lib/select'
import ID_COMPONENTE from 'config/idComponente'
import MACRO_CODIGO from 'config/macroCodigo'
import TIPO_MACRO from 'config/tipoMacro'
import TIPO_USUARIO from 'config/tipoUsuario'
import { ApplicationContext } from 'Context'
import buscaAgendamentoMultiplo from 'Helpers/agendamentoMultiplo'
import Claims from 'Hooks/usePermissions/Claims'
import usePermissions from 'Hooks/usePermissions/usePermissions'
import { IPerfil } from 'Models/auth'
import { ICandidato, IMacro } from 'Models/docas'
import { STATUS_HORARIOS } from 'Models/docas/IHorario'
import moment from 'moment'
import WsDocasService from 'Services/WS_DOCAS'
import { ServiceListResponse } from 'Types/ServiceResponse'

import { DocasContext } from '../Context/context'

interface IProps {
  candidato?: ICandidato
  atualizaAgenda: (
    limparSelecao?: boolean,
    executandoAcao?: boolean,
  ) => Promise<void>
}
const ModalMacrosCandidato = ({
  candidato,
  atualizaAgenda,
}: IProps): ReactElement => {
  const [formulario] = Form.useForm()
  const [envia, setEnvia] = useState<boolean>(false)
  const [macro, setMacro] = useState<number>()
  const [macros, setMacros] = useState<IMacro[]>([])
  const [macrosEmitidas, setMacrosEmitidas] = useState<IMacro[]>([])
  const [justificativa, setJustificativa] = useState<string>('')
  const [justificativaObrigatoria, setJustificativaObrigatoria] = useState<
    boolean | undefined
  >(true)
  const { applicationContext } = useContext(ApplicationContext)
  const { docasContext, setDocasContext } = useContext(DocasContext)
  const { Option } = Select
  const [emitindoMacro, setEmitindoMacro] = useState<boolean>(false)
  const [carregandoMacros, setCarregandoMacros] = useState<boolean>(true)
  const [carregandoMacrosEmitidas, setCarregandoMacrosEmitidas] =
    useState<boolean>(true)

  const { isAuthorized, isSystemMaster } = usePermissions()

  useEffect(() => {
    buscaMacros()
    buscaMacrosEmitidas()
  }, [])

  const buscaMacros = async () => {
    const { data } = await WsDocasService.macro.getList(
      ID_COMPONENTE.DOCAS,
      docasContext.cdArmazemAtual,
    )

    if (data?.items) {
      setMacros(data.items)
    }

    setCarregandoMacros(false)
  }

  const buscaMacrosEmitidas = async () => {
    if (candidato) {
      const { data } = await WsDocasService.macro.buscaMacrosEmitidas(
        ID_COMPONENTE.DOCAS,
        candidato.cdCandidatoAgendamento,
      )

      if (data?.items) {
        setMacrosEmitidas(data.items)
      }
    }

    setCarregandoMacrosEmitidas(false)
  }

  const handleChangeMacro = (value: SelectValue) => {
    const cdMacro = value as number

    setMacro(cdMacro)
    setJustificativaObrigatoria(
      macros.find((f) => f.cdMacro === cdMacro)?.justObrigatoria,
    )

    if (justificativa.length >= 20 && value) {
      setEnvia(true)
    } else {
      setEnvia(false)
    }
  }

  const handleChangeJustificativa = (e: {
    target: { value: React.SetStateAction<string> }
  }) => {
    setJustificativa(e.target.value.toString().trimStart())
    if (e.target.value.toString().trimStart().length >= 20 && macro) {
      setEnvia(true)
    } else {
      setEnvia(false)
    }
  }

  const executaMacro = async () => {
    if (macro && candidato) {
      setEnvia(false)
      setEmitindoMacro(true)

      const { data } = await WsDocasService.macro.executaMacro(
        ID_COMPONENTE.DOCAS,
        {
          cdArmazem: candidato.cdAgendamentoDocasArmazem,
          cdCandidato: candidato.cdCandidatoAgendamento,
          cdMacro: macro,
          justificativa,
          placaVeiculo: candidato.placa,
          transportadora: candidato.nomeTransportadora,
          cdEntidade: candidato.cdEntidade,
          cdVeiculo: candidato.cdVeiculo,
          cdViagem: candidato.cdViagem,
        },
      )

      if (data.message !== 'Sucesso') {
        window.alert(`Erro no registro de macro. ${data.message}`)
      }

      atualizaAgenda()
      await buscaMacrosEmitidas()

      setMacro(undefined)
      setJustificativa('')

      setEmitindoMacro(false)
    }
  }

  return (
    <>
      <PageHeader
        ghost={false}
        title={`Macros da Placa ${candidato?.placa}`}
        extra={
          isAuthorized(Claims.EmitirMacrosDocas)
            ? [
                <Button
                  key="emitirMacro"
                  style={{ marginTop: '30px' }}
                  disabled={!envia && justificativaObrigatoria}
                  type="primary"
                  onClick={() => executaMacro()}
                  loading={emitindoMacro}
                >
                  Emitir macro
                </Button>,
              ]
            : []
        }
      />

      <Skeleton loading={carregandoMacros || carregandoMacrosEmitidas}>
        {isAuthorized(Claims.EmitirMacrosDocas) && (
          <Form form={formulario} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Macro:" required tooltip="Campo obrigatório">
                  <Select
                    style={{ width: '100%' }}
                    onChange={handleChangeMacro}
                    value={macro}
                  >
                    {macros.length > 0
                      ? macros.map((m) => (
                          <Option key={m.cdMacro} value={m.cdMacro!}>
                            {m.nome!}
                          </Option>
                        ))
                      : []}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  label="Justificativa:"
                  required={justificativaObrigatoria}
                  tooltip={
                    justificativaObrigatoria
                      ? 'Campo obrigatório, mínimo 20 caracteres'
                      : undefined
                  }
                >
                  <Input
                    type="text"
                    minLength={20}
                    value={justificativa}
                    onChange={handleChangeJustificativa}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}

        <Row>
          <Col>
            <Table
              columns={[
                {
                  title: 'Macro',
                  dataIndex: 'nome',
                  key: 'nome',
                },
                {
                  title: 'Data/Hora',
                  dataIndex: 'data',
                  key: 'data',
                },
                {
                  title: 'Usuário',
                  dataIndex: 'usuarioNome',
                  key: 'usuarioNome',
                  render: (text: string) => <span>{text}</span>,
                },
                {
                  title: 'Justificativa',
                  dataIndex: 'justificativa',
                  key: 'justificativa',
                  render: (text: string) => <span>{text}</span>,
                },
              ]}
              dataSource={macrosEmitidas}
              pagination={false}
              size="small"
              sticky
              scroll={{ y: 350 }}
            />
          </Col>
        </Row>
      </Skeleton>
    </>
  )
}

export default ModalMacrosCandidato
